"use client";

import { useEffect, useRef } from "react";
import { signOut, useSession } from "next-auth/react";

import { ACCOUNT_DELETED_PATH } from "@/lib/account-deleted-constants";

const POLL_MS = 5000;

/**
 * Soft-delete does not expire the NextAuth JWT cookie. SessionProvider also
 * never refetches after mount, so an open tab would otherwise stay signed in.
 * Poll account status and sign out as soon as the account is gone.
 */
export function DeletedAccountGuard() {
  const { status } = useSession();
  const signingOutRef = useRef(false);

  useEffect(() => {
    if (status !== "authenticated") return;

    const signOutDeleted = () => {
      if (signingOutRef.current) return;
      signingOutRef.current = true;
      signOut({ callbackUrl: ACCOUNT_DELETED_PATH });
    };

    const check = async () => {
      if (signingOutRef.current) return;
      try {
        const res = await fetch("/api/account/status", { cache: "no-store" });
        const data = (await res.json().catch(() => null)) as { status?: string } | null;
        if (data?.status === "deleted") signOutDeleted();
      } catch {
        // Network blip — retry on the next poll.
      }
    };

    void check();
    const interval = setInterval(check, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status]);

  return null;
}
